import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { GoldPriceResponse, GoldShop, InternationalGold, DomesticGold } from './types/gold';
import { ArrowUpIcon, ArrowDownIcon, ArrowPathIcon } from '@heroicons/react/24/solid';

function App() {
  const [goldData, setGoldData] = useState<GoldPriceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');

  const fetchGoldPrice = async () => {
    try {
      setLoading(true);
      setRefreshing(true);
      console.log('开始获取黄金价格数据...');
      const response = await axios.get<GoldPriceResponse>('https://www.john-xg.cn/api/gold-price/');
      console.log('获取到的数据:', response.data);
      
      if (response.data && response.data.code === 200) {
        setGoldData(response.data);
        const now = new Date();
        const formattedTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
        setLastUpdateTime(formattedTime);
        setLoading(false);
        setRefreshing(false);
      } else {
        setError('数据格式不正确');
        setLoading(false);
        setRefreshing(false);
      }
    } catch (err) {
      console.error('获取数据失败:', err);
      setError('获取黄金价格数据失败: ' + (err instanceof Error ? err.message : String(err)));
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchGoldPrice();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600 flex items-center">
          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          加载中...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-2xl text-red-600 bg-white p-6 rounded-lg shadow-md">
          <svg className="h-8 w-8 mx-auto mb-2 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  if (!goldData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600 bg-white p-6 rounded-lg shadow-md">
          暂无数据
        </div>
      </div>
    );
  }

  // 获取国内金价数据
  const domesticGold = goldData.国内黄金.find(item => item.品种 === '国内金价');
  // 获取国际金价数据
  const internationalGold = goldData.国际黄金.find(item => item.品种 === '国际金价');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">黄金价格实时行情</h1>
          <div className="mt-4 flex items-center justify-center space-x-4">
            <p className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full shadow-sm">
              最后更新时间: {lastUpdateTime || domesticGold?.报价时间 || '未知'}
            </p>
            <button
              onClick={fetchGoldPrice}
              disabled={refreshing}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                refreshing 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              } transition-colors duration-200 shadow-sm`}
            >
              <ArrowPathIcon className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? '刷新中...' : '刷新数据'}
            </button>
          </div>
        </div>

        {/* 基准金价展示 */}
        <div className="mb-8">
          <div className="bg-white overflow-hidden shadow-lg rounded-lg border border-gray-100 transform transition-all duration-300 hover:shadow-xl">
            <div className="px-6 py-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">基准金价</h2>
                  <p className="text-sm text-gray-500 mt-1">中国黄金基础金价</p>
                </div>
                <div className="text-right">
                  <span className="text-4xl font-bold text-gray-900">¥{domesticGold?.最新价}</span>
                  <p className="text-sm text-gray-500 mt-1">元/克</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {goldData.国内十大金店.map((item: GoldShop) => (
            <div
              key={item.品牌}
              className="bg-white overflow-hidden shadow-lg rounded-lg border border-gray-100 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
            >
              <div className="px-6 py-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-medium text-gray-900">
                    {item.品牌}
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{item.单位}</span>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-2xl font-semibold text-gray-900">
                    ¥{item.黄金价格}
                  </div>
                  <div className="flex items-center">
                    {parseFloat(item.黄金价格) > parseFloat(domesticGold?.最新价 || '0') ? (
                      <div className="flex items-center bg-green-50 px-3 py-1 rounded-full">
                        <ArrowUpIcon className="h-5 w-5 text-green-500" />
                        <span className="ml-1 text-sm text-green-500">涨</span>
                      </div>
                    ) : parseFloat(item.黄金价格) < parseFloat(domesticGold?.最新价 || '0') ? (
                      <div className="flex items-center bg-red-50 px-3 py-1 rounded-full">
                        <ArrowDownIcon className="h-5 w-5 text-red-500" />
                        <span className="ml-1 text-sm text-red-500">跌</span>
                      </div>
                    ) : (
                      <div className="flex items-center bg-gray-50 px-3 py-1 rounded-full">
                        <span className="text-sm text-gray-500">--</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default App; 